import { useQuery } from '@tanstack/react-query';
import { supabase } from '../utils/supabaseClient';
import { queryKeys } from '../utils/queryKeys';
import { useQueryError } from './useQueryError';

const fetchMostPopularCardData = async () => {
    try {
        const { data, error } = await supabase
            .from("cards")
            .select(`
                id,
                companyname,
                companyoverviewid,
                summary,
                card_color,
                addedtime,
                categories (
                    name
                ),
                company_overview (
                    description,
                    button_color,
                    companylink
                )
            `)
            .order("rank", { ascending: false })
            .limit(1)
            .single();

        if (error) {
            console.error('Error fetching most popular card:', error);
            throw error;
        }

        if (!data) {
            throw new Error('No data found for most popular card');
        }

        return {
            ...data,
            card_color: data.card_color || "#3A3A39",
            company_overview: {
                ...data.company_overview,
                button_color: data.company_overview?.button_color || "#7443E8"
            }
        };
    } catch (error) {
        console.error('Error in fetchMostPopularCardData:', error);
        throw error;
    }
};

export function useMostPopularCard() {
    const { handleError } = useQueryError();

    const { data, isLoading, error } = useQuery({
        queryKey: queryKeys.cards.mostPopular(),
        queryFn: fetchMostPopularCardData,
        onError: handleError,
        staleTime: 1000 * 60 * 5, // 5 minutos
        cacheTime: 1000 * 60 * 30, // 30 minutos
        retry: 2
    });

    const formatAddedTime = (addedtime) => {
        if (!addedtime) return "";
        const now = new Date();
        const addedDate = new Date(addedtime);
        const diffTime = Math.abs(now.getTime() - addedDate.getTime());
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        const diffWeeks = Math.floor(diffDays / 7);

        if (diffDays < 1) return "Added today";
        if (diffDays === 1) return "Added yesterday";
        if (diffDays < 7) return `Added ${diffDays} days ago`;
        if (diffWeeks === 1) return "Added 1 week ago";
        if (diffWeeks < 4) return `Added ${diffWeeks} weeks ago`;

        const month = addedDate.toLocaleString('default', { month: 'long' });
        const day = addedDate.getDate();
        
        if (diffDays < 365) {
            return `Added ${month} ${day}`;
        }
        
        const year = addedDate.getFullYear();
        return `Added ${month} ${day}, ${year}`;
    };

    return {
        cardData: data ? {
            ...data,
            formattedAddedTime: formatAddedTime(data.addedtime)
        } : null,
        isLoading,
        error
    };
}